import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, ShoppingCart, Zap, Heart, Phone } from 'lucide-react';
import Swal from 'sweetalert2';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/product/ProductCard';
import ImageGallery from '../components/product/ImageGallery';
import ReviewList from '../components/review/ReviewList';
import ReviewForm from '../components/review/ReviewForm';
import { getImageUrl } from '../utils/imageUrl';
import { Product, Review, CustomAttributeGroup } from '../types';
import { productAPI, reviewAPI } from '../services/api';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';
import {
  calculateProductPriceWithAttributes,
  normalizeOptions,
  formatExtraPriceBadge,
  getColorHex,
  isLightColor,
} from '../utils/attributeHelpers';

import './ProductDetails.css';

const ProductDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isInWishlist } = useWishlist();

  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Gallery active index state for dynamic color-image syncing
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  // Selected attribute options & validation error state
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedWeight, setSelectedWeight] = useState<string>('');
  const [selectedCustom, setSelectedCustom] = useState<Record<string, string>>({});
  const [missingAttributes, setMissingAttributes] = useState<Record<string, boolean>>({});

  const currentLang = i18n.language;

  // Load product data
  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const data = await productAPI.getProductBySlug(slug!);
        setProduct(data);
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProduct();
    }
  }, [slug]);

  // Load reviews when product is loaded
  const loadReviews = async (productId: number) => {
    try {
      setReviewsLoading(true);
      const data = await reviewAPI.getProductReviews(productId);
      const reviewsArray = Array.isArray(data) ? data : ((data as any)?.results || (data as any)?.data || []);
      setReviews(reviewsArray);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      loadReviews(product.id);
    }
  }, [product]);

  // Reset variant selections & active image when product changes
  useEffect(() => {
    setSelectedColor('');
    setSelectedSize('');
    setSelectedWeight('');
    setSelectedCustom({});
    setMissingAttributes({});
    setActiveImageIndex(0);
  }, [product?.id]);

  // Track ViewContent events
  useEffect(() => {
    if (product) {
      const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
      const categoryName = (product as any).category
        ? (currentLang === 'bn' ? (product as any).category.name_bn : (product as any).category.name_en)
        : product.category_name || '';

      trackFacebookEvent('ViewContent', {
        content_id: product.id.toString(),
        content_name: productName,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price,
        currency: 'BDT',
        content_category: categoryName,
      });

      gtmEvents.viewItem({
        id: product.id,
        name: productName,
        price: product.final_price,
        category: categoryName,
      });
    }
  }, [product, currentLang]);

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  if (!product) {
    return <div className="error-container"><p>{t('common.error')}</p></div>;
  }

  const productName = currentLang === 'bn' ? (product.name_bn || product.name_en) : (product.name_en || product.name_bn);
  const productDesc = currentLang === 'bn' ? product.description_bn : product.description_en;
  const productSummary = currentLang === 'bn' ? product.summary_bn : product.summary_en;

  const categoryName = typeof product.category === 'object' && product.category !== null
    ? (currentLang === 'bn' ? product.category.name_bn : product.category.name_en)
    : product.category_name || '';

  // Prepare gallery images list
  const mainImage = product.image ? [{
    id: -1,
    image: getImageUrl(product.image),
    alt_text: productName,
    is_primary: true,
    display_order: 0,
  }] : [];

  const additionalImages = (product.images && Array.isArray(product.images) && product.images.length > 0)
    ? product.images.filter(img => img && img.image).map(img => ({
        ...img,
        image: getImageUrl(img.image),
        alt_text: img.alt_text || productName,
      }))
    : [];

  const galleryImages = [...mainImage, ...additionalImages];

  // Normalized color and size options
  const colorOptions = normalizeOptions(product.attributes?.colors?.options);
  const sizeOptions = normalizeOptions(product.attributes?.sizes?.options);
  const weightOptions = normalizeOptions(product.attributes?.weights?.options);

  // ── Two-way Dynamic Color & Image Mapping ──
  const handleColorSelect = (colorName: string) => {
    const isDeselect = selectedColor === colorName;
    const nextColor = isDeselect ? '' : colorName;
    setSelectedColor(nextColor);
    setMissingAttributes(prev => ({ ...prev, color: false }));

    if (!isDeselect && galleryImages.length > 0) {
      const target = colorName.toLowerCase().trim();

      // 1. Check if an image's alt_text mentions this color
      let matchIdx = galleryImages.findIndex(img =>
        img.alt_text && img.alt_text.toLowerCase().includes(target)
      );

      // 2. Check if image URL/filename mentions this color
      if (matchIdx === -1) {
        matchIdx = galleryImages.findIndex(img =>
          img.image && img.image.toLowerCase().includes(target)
        );
      }

      // 3. Fallback: if color count matches or corresponds to index
      if (matchIdx === -1 && colorOptions.length > 0) {
        const cIndex = colorOptions.findIndex(c => c.name.toLowerCase() === target);
        if (cIndex >= 0 && cIndex < galleryImages.length) {
          matchIdx = cIndex;
        }
      }

      if (matchIdx !== -1) {
        setActiveImageIndex(matchIdx);
      }
    }
  };

  const handleGalleryImageSelect = (idx: number) => {
    setActiveImageIndex(idx);
    const activeImg = galleryImages[idx];
    if (!activeImg || colorOptions.length === 0) return;

    // Check if clicked image correlates with a color
    const matched = colorOptions.find(opt => {
      const optName = opt.name.toLowerCase().trim();
      return (
        (activeImg.alt_text && activeImg.alt_text.toLowerCase().includes(optName)) ||
        (activeImg.image && activeImg.image.toLowerCase().includes(optName))
      );
    });

    if (matched) {
      setSelectedColor(matched.name);
      setMissingAttributes(prev => ({ ...prev, color: false }));
    } else if (colorOptions.length === galleryImages.length) {
      setSelectedColor(colorOptions[idx].name);
      setMissingAttributes(prev => ({ ...prev, color: false }));
    }
  };

  // Size selection with out-of-stock guard
  const handleSizeSelect = (optName: string, isOutOfStock?: boolean) => {
    if (isOutOfStock) {
      Swal.fire({
        icon: 'info',
        title: currentLang === 'bn' ? 'স্টক শেষ' : 'Out of Stock',
        text: currentLang === 'bn' ? 'এই সাইজটি বর্তমানে স্টকে নেই।' : 'This size is currently out of stock.',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
      return;
    }
    setSelectedSize(prev => (prev === optName ? '' : optName));
    setMissingAttributes(prev => ({ ...prev, size: false }));
  };

  // Variant Selection Validation
  const validateVariantSelection = (): boolean => {
    if (!product) return false;

    const hasColor = !!(product.attributes?.colors?.enabled && colorOptions.length > 0);
    const hasSize = !!(product.attributes?.sizes?.enabled && sizeOptions.length > 0);
    const hasWeight = !!(product.attributes?.weights?.enabled && weightOptions.length > 0);

    const missingColor = hasColor && !selectedColor;
    const missingSize = hasSize && !selectedSize;
    const missingWeight = hasWeight && !selectedWeight;

    const missingCustom: Record<string, boolean> = {};
    let hasMissingCustom = false;
    (product.attributes?.custom || []).forEach(cg => {
      if (cg.enabled && (cg.options || []).length > 0) {
        const key = cg.id || cg.name;
        if (!selectedCustom[key]) {
          missingCustom[key] = true;
          hasMissingCustom = true;
        }
      }
    });

    if (missingColor || missingSize || missingWeight || hasMissingCustom) {
      setMissingAttributes({
        color: missingColor,
        size: missingSize,
        weight: missingWeight,
        ...missingCustom,
      });

      const card = document.querySelector('.pd-variants-card');
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      Swal.fire({
        icon: 'warning',
        title: currentLang === 'bn' ? 'অপশন নির্বাচন করুন' : 'Select Options',
        text: currentLang === 'bn'
          ? 'অনুগ্রহ করে প্রয়োজনীয় কালার ও সাইজ নির্বাচন করুন।'
          : 'Please select required color and size before proceeding.',
        timer: 2500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });

      return false;
    }

    setMissingAttributes({});
    return true;
  };

  // Dynamic live pricing with attribute options
  const currentUnitDiscountPrice = product.discount_price ? calculateProductPriceWithAttributes(
    Number(product.discount_price),
    product.attributes,
    selectedColor,
    selectedSize,
    selectedWeight,
    selectedCustom
  ) : undefined;

  const currentUnitRegularPrice = calculateProductPriceWithAttributes(
    Number(product.price),
    product.attributes,
    selectedColor,
    selectedSize,
    selectedWeight,
    selectedCustom
  );

  const currentFinalPrice = currentUnitDiscountPrice || currentUnitRegularPrice;

  // Add to cart animation flyer
  const runAddToCartAnimation = (sourceElement: HTMLElement) => {
    if (!product || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targetElement = Array.from(
      document.querySelectorAll<HTMLElement>(
        '.mobile-bottom-navigation .mobile-cart-btn, .header-user-actions .cart-btn-top'
      )
    ).find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && window.getComputedStyle(element).display !== 'none';
    });
    if (!targetElement) return;

    const sourceRect = sourceElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;

    const flyer = document.createElement('div');
    flyer.className = 'fly-to-cart-animation';

    const imageUrl = getImageUrl(
      product.image ||
      product.primary_image ||
      product.images?.find(img => img.is_primary)?.image ||
      product.images?.[0]?.image
    );
    if (imageUrl) {
      flyer.style.backgroundImage = `url(${imageUrl})`;
    } else {
      flyer.classList.add('without-image');
    }

    flyer.style.left = `${startX - 26}px`;
    flyer.style.top = `${startY - 26}px`;
    flyer.style.setProperty('--fly-x', `${endX - startX}px`);
    flyer.style.setProperty('--fly-y', `${endY - startY}px`);
    document.body.appendChild(flyer);
    flyer.classList.add('is-flying');

    window.setTimeout(() => {
      targetElement.classList.add('cart-target-bump');
    }, 820);

    window.setTimeout(() => {
      flyer.remove();
      targetElement.classList.remove('cart-target-bump');
    }, 1180);
  };

  const handleAddToCart = (event?: React.MouseEvent<HTMLElement>) => {
    if (!validateVariantSelection()) return;
    if (product) {
      if (event?.currentTarget) {
        runAddToCartAnimation(event.currentTarget);
      }

      addItem(
        product,
        quantity,
        {
          color: selectedColor,
          size: selectedSize,
          weight: selectedWeight,
          custom: selectedCustom,
          unit_price: currentFinalPrice,
        }
      );

      trackFacebookEvent('AddToCart', {
        content_id: product.id.toString(),
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: currentFinalPrice * quantity,
        currency: 'BDT',
      });

      Swal.fire({
        position: 'top-end',
        icon: 'success',
        title: currentLang === 'bn' ? 'কার্টে যোগ হয়েছে' : 'Added to Cart',
        showConfirmButton: false,
        timer: 1500,
        toast: true,
      });
    }
  };

  const handleDirectOrder = () => {
    if (!validateVariantSelection()) return;
    if (product) {
      trackFacebookEvent('AddToCart', {
        content_id: product.id.toString(),
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: currentFinalPrice * quantity,
        currency: 'BDT',
      });

      const allSelectedAttributes: Record<string, string> = {
        ...(selectedColor ? { [currentLang === 'bn' ? 'রং' : 'Color']: selectedColor } : {}),
        ...(selectedSize ? { [currentLang === 'bn' ? 'সাইজ' : 'Size']: selectedSize } : {}),
        ...(selectedWeight ? { [currentLang === 'bn' ? 'ওজন' : 'Weight']: selectedWeight } : {}),
        ...selectedCustom,
      };

      const orderData = {
        product: {
          ...product,
          final_price: currentFinalPrice,
        },
        quantity: quantity,
        unit_price: currentFinalPrice,
        selected_color: selectedColor,
        selected_size: selectedSize,
        selected_weight: selectedWeight,
        selected_attributes: allSelectedAttributes,
      };

      sessionStorage.setItem('directOrder', JSON.stringify(orderData));
      navigate('/checkout');
    }
  };

  const handleWishlistToggle = () => {
    if (product) {
      const inWishlist = isInWishlist(product.id);
      if (inWishlist) {
        removeFromWishlist(product.id);
        Swal.fire({
          position: 'top-end',
          icon: 'info',
          title: currentLang === 'bn' ? 'অপসারিত' : 'Removed',
          timer: 1800,
          showConfirmButton: false,
          toast: true,
        });
      } else {
        addToWishlist(product);
        trackFacebookEvent('AddToWishlist', {
          content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
          content_ids: [product.id.toString()],
          content_type: 'product',
          value: product.final_price,
          currency: 'BDT',
        });
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: currentLang === 'bn' ? 'যোগ হয়েছে' : 'Added',
          timer: 1800,
          showConfirmButton: false,
          toast: true,
        });
      }
    }
  };

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, Math.min(product?.stock || 99, prev + delta)));
  };

  // Clean summary helper that decodes &nbsp; and supports rich text/paragraphs
  const getCleanSummary = (): { html: string; hasContent: boolean } => {
    let raw = '';
    if (productSummary && productSummary.trim() !== '') {
      raw = productSummary;
    } else if (productDesc && productDesc.trim() !== '') {
      const firstLine = productDesc.split(/<\/p>|<br\s*\/?>|\r?\n/i)[0];
      raw = firstLine && firstLine.length > 20 ? firstLine : productDesc.slice(0, 300);
    }

    if (!raw) return { html: '', hasContent: false };

    // Decode &nbsp; into standard space to prevent literal entity rendering and avoid line wrap issues
    let normalized = raw
      .replace(/&amp;nbsp;/gi, ' ')
      .replace(/&nbsp;/gi, ' ');

    // Check if there is actual text content
    const textOnly = normalized.replace(/<[^>]*>/g, '').trim();
    if (!textOnly) return { html: '', hasContent: false };

    // If text has no HTML tags, convert newlines to <br />
    if (!/<[a-z][\s\S]*>/i.test(normalized)) {
      normalized = normalized.replace(/\r?\n/g, '<br />');
    }

    return { html: normalized, hasContent: true };
  };

  const summaryData = getCleanSummary();

  return (
    <div className="product-details-page pd-redesign">
      <div className="container">
        {/* Top Breadcrumb Matching Screenshot */}
        <nav className="pd-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="pd-breadcrumb-link" title="Home">
            <Home size={15} className="pd-home-icon" />
          </Link>
          <span className="pd-breadcrumb-sep">/</span>
          <Link to="/products" className="pd-breadcrumb-link">
            {currentLang === 'bn' ? 'সকল পণ্য' : 'All Products'}
          </Link>
          {categoryName && (
            <>
              <span className="pd-breadcrumb-sep">/</span>
              <Link
                to={`/products?category=${typeof product.category === 'object' ? product.category?.slug : ''}`}
                className="pd-breadcrumb-link"
              >
                {categoryName}
              </Link>
            </>
          )}
          <span className="pd-breadcrumb-sep">/</span>
          <span className="pd-breadcrumb-current">{productName}</span>
        </nav>

        {/* 2-Column Product Layout: Gallery (Left) & Info (Right) */}
        <div className="pd-main-section">
          <div className="pd-gallery-column">
            <ImageGallery
              images={galleryImages}
              productName={productName}
              selectedIndex={activeImageIndex}
              onSelectImage={handleGalleryImageSelect}
            />
          </div>

          <div className="pd-info-column">
            {/* Product Title Header */}
            <div className="pd-header-row">
              <h1 className="pd-product-title">{productName}</h1>
              <button
                type="button"
                className={`pd-wishlist-toggle ${isInWishlist(product.id) ? 'active' : ''}`}
                onClick={handleWishlistToggle}
                aria-label="Wishlist"
                title="Wishlist"
              >
                <Heart size={20} fill={isInWishlist(product.id) ? '#ef4444' : 'none'} color={isInWishlist(product.id) ? '#ef4444' : '#64748b'} />
              </button>
            </div>

            {/* Price & Stock Badge Row */}
            <div className="pd-price-stock-row">
              <div className="pd-price-group">
                <span className="pd-sale-price">
                  ৳{currentFinalPrice.toLocaleString()}
                </span>
                {currentUnitDiscountPrice && (
                  <span className="pd-regular-price">
                    ৳{currentUnitRegularPrice.toLocaleString()}
                  </span>
                )}
              </div>

              <div className={`pd-stock-badge ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                {product.stock > 0 ? (
                  <>
                    <span className="pd-stock-check">✓</span>
                    <span>{currentLang === 'bn' ? `স্টক: ${product.stock}` : `Stock: ${product.stock}`}</span>
                  </>
                ) : (
                  <>
                    <span className="pd-stock-cross">✗</span>
                    <span>{currentLang === 'bn' ? 'স্টক শেষ' : 'Out of Stock'}</span>
                  </>
                )}
              </div>
            </div>

            {/* Product Summary Card - Placed right above Variant Selection Card */}
            {summaryData.hasContent && (
              <div className="pd-summary-card">
                <div className="pd-summary-title">
                  <span className="pd-vertical-bar">|</span>
                  <span>{currentLang === 'bn' ? 'সংক্ষিপ্ত বিবরণ' : 'Summary'}</span>
                </div>
                <div
                  className="pd-summary-body"
                  dangerouslySetInnerHTML={{ __html: summaryData.html }}
                />
              </div>
            )}

            {/* Variant Selection Card (Color & Size) */}
            <div className={`pd-variants-card ${missingAttributes.color || missingAttributes.size ? 'has-error' : ''}`}>
              {/* Color Selection Section */}
              {product.attributes?.colors?.enabled && colorOptions.length > 0 && (
                <div className={`pd-attr-block ${missingAttributes.color ? 'attr-missing' : ''}`}>
                  <div className="pd-attr-header">
                    <div className="pd-attr-title">
                      <span className="pd-vertical-bar">|</span>
                      <span className="pd-title-text">
                        {currentLang === 'bn' ? 'রং সিলেক্ট করুন' : 'Select Color'}
                      </span>
                      {selectedColor && (
                        <span className="pd-selected-hint"> — {selectedColor}</span>
                      )}
                    </div>
                    <div className="pd-code-badge">
                      <span className="pd-vertical-bar">|</span>
                      <span>Code {product.sku || product.barcode || product.id}</span>
                    </div>
                  </div>

                  <div className="pd-color-pills-row">
                    {colorOptions.map((opt) => {
                      const isSelected = selectedColor.toLowerCase() === opt.name.toLowerCase();
                      const hex = getColorHex(opt.name);
                      const needsBorder = isLightColor(hex);

                      return (
                        <button
                          key={opt.name}
                          type="button"
                          className={`pd-color-pill ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleColorSelect(opt.name)}
                          title={opt.name}
                        >
                          <span
                            className={`pd-color-dot ${needsBorder ? 'needs-border' : ''}`}
                            style={{ backgroundColor: hex }}
                          />
                          <span className="pd-color-label">{opt.name}</span>
                          {opt.extra_price > 0 && (
                            <span className="pd-extra-price">
                              {formatExtraPriceBadge(opt.extra_price, currentLang)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Dashed Divider between Color and Size */}
              {product.attributes?.colors?.enabled && colorOptions.length > 0 &&
               product.attributes?.sizes?.enabled && sizeOptions.length > 0 && (
                <div className="pd-variants-divider" />
              )}

              {/* Size Selection Section */}
              {product.attributes?.sizes?.enabled && sizeOptions.length > 0 && (
                <div className={`pd-attr-block ${missingAttributes.size ? 'attr-missing' : ''}`}>
                  <div className="pd-attr-header">
                    <div className="pd-attr-title">
                      <span className="pd-vertical-bar">|</span>
                      <span className="pd-title-text">
                        {currentLang === 'bn' ? 'সাইজ সিলেক্ট করুন' : 'Select Size'}
                      </span>
                      {selectedSize && (
                        <span className="pd-selected-hint"> — {selectedSize}</span>
                      )}
                    </div>
                  </div>

                  <div className="pd-size-chips-row">
                    {sizeOptions.map((opt) => {
                      const isSelected = selectedSize.toLowerCase() === opt.name.toLowerCase();
                      const isOutOfStock = opt.is_out_of_stock || false;

                      return (
                        <button
                          key={opt.name}
                          type="button"
                          disabled={isOutOfStock}
                          className={`pd-size-chip ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                          onClick={() => handleSizeSelect(opt.name, isOutOfStock)}
                          title={isOutOfStock ? `${opt.name} (Out of Stock)` : opt.name}
                        >
                          <span className="pd-size-name">{opt.name}</span>
                          {opt.extra_price > 0 && (
                            <span className="pd-extra-price">
                              {formatExtraPriceBadge(opt.extra_price, currentLang)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Optional: Weights or Custom Attributes */}
              {product.attributes?.weights?.enabled && weightOptions.length > 0 && (
                <>
                  <div className="pd-variants-divider" />
                  <div className="pd-attr-block">
                    <div className="pd-attr-header">
                      <div className="pd-attr-title">
                        <span className="pd-vertical-bar">|</span>
                        <span className="pd-title-text">
                          {currentLang === 'bn' ? 'ওজন নির্বাচন' : 'Select Weight'}
                        </span>
                      </div>
                    </div>
                    <div className="pd-size-chips-row">
                      {weightOptions.map(opt => (
                        <button
                          key={opt.name}
                          type="button"
                          className={`pd-size-chip ${selectedWeight === opt.name ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedWeight(prev => prev === opt.name ? '' : opt.name);
                            setMissingAttributes(prev => ({ ...prev, weight: false }));
                          }}
                        >
                          {opt.name}
                          {opt.extra_price > 0 && formatExtraPriceBadge(opt.extra_price, currentLang)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {(product.attributes?.custom || []).filter(cg => cg.enabled && (cg.options || []).length > 0).map((cg: CustomAttributeGroup) => {
                const groupKey = cg.id || cg.name;
                const opts = normalizeOptions(cg.options);
                return (
                  <React.Fragment key={cg.id}>
                    <div className="pd-variants-divider" />
                    <div className="pd-attr-block">
                      <div className="pd-attr-header">
                        <div className="pd-attr-title">
                          <span className="pd-vertical-bar">|</span>
                          <span className="pd-title-text">
                            {currentLang === 'bn' ? (cg.name_bn || cg.name) : (cg.name_en || cg.name)}
                          </span>
                        </div>
                      </div>
                      <div className="pd-size-chips-row">
                        {opts.map(opt => (
                          <button
                            key={opt.name}
                            type="button"
                            className={`pd-size-chip ${selectedCustom[groupKey] === opt.name ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedCustom(prev => ({
                                ...prev,
                                [groupKey]: prev[groupKey] === opt.name ? '' : opt.name,
                              }));
                              setMissingAttributes(prev => ({ ...prev, [groupKey]: false }));
                            }}
                          >
                            {opt.name}
                            {opt.extra_price > 0 && formatExtraPriceBadge(opt.extra_price, currentLang)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Action Buttons Section */}
            <div className="pd-actions-area">
              {/* Row 1: Quantity Selector + Add to Cart Button */}
              <div className="pd-actions-row-1">
                <div className="pd-qty-selector">
                  <button
                    type="button"
                    className="pd-qty-btn"
                    onClick={() => handleQuantityChange(-1)}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="pd-qty-value">{quantity}</span>
                  <button
                    type="button"
                    className="pd-qty-btn"
                    onClick={() => handleQuantityChange(1)}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  className="pd-add-to-cart-btn"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart size={20} className="pd-cart-icon" />
                  <span>{currentLang === 'bn' ? 'কার্টে যোগ করুন' : 'Add to Cart'}</span>
                </button>
              </div>

              {/* Row 2: Full-width Order Now Button */}
              <button
                type="button"
                className="pd-order-now-btn"
                onClick={handleDirectOrder}
              >
                <Zap size={21} className="pd-zap-icon" />
                <span>{currentLang === 'bn' ? 'অর্ডার করুন' : 'Order Now'}</span>
              </button>

              {/* Row 3: WhatsApp + Call Now Side-by-Side */}
              <div className="pd-contact-row">
                <button
                  type="button"
                  className="pd-whatsapp-btn"
                  onClick={() => {
                    const whatsappNum = sessionStorage.getItem('whatsapp_number') || '8801741818807';
                    window.open(`https://wa.me/${whatsappNum.replace(/\+/g, '').replace(/[^0-9]/g, '')}`, '_blank');
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="pd-contact-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: 'block', flexShrink: 0 }}
                    aria-hidden="true"
                  >
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                  <span>WhatsApp</span>
                </button>

                <a
                  href={`tel:${sessionStorage.getItem('support_phone') || '+8801741818807'}`}
                  className="pd-call-now-btn"
                >
                  <Phone size={19} strokeWidth={2.4} className="pd-contact-icon" style={{ display: 'block', flexShrink: 0 }} />
                  <span>{currentLang === 'bn' ? 'কল করুন' : 'Call Now'}</span>
                </a>
              </div>
            </div>
          </div>
        </div>


        {/* Detailed Description (if exists) */}
        {productDesc && productDesc.replace(/<[^>]*>/g, '').trim() !== '' && (
          <section className="product-full-description">
            <h2>{currentLang === 'bn' ? 'বিস্তারিত বিবরণ' : 'Detailed Description'}</h2>
            <div
              className="description-content"
              dangerouslySetInnerHTML={{ __html: productDesc }}
            />
          </section>
        )}

        {/* Related Products Section */}
        {product.related_products && product.related_products.length > 0 && (
          <section className="related-products">
            <h2>{t('product.relatedProducts')}</h2>
            <div className="products-grid">
              {product.related_products.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}

        {/* Customer Reviews Section */}
        <section className="reviews-section" id="reviews">
          <div className="reviews-header-bar">
            <div>
              <h2 className="reviews-title">{t('reviews.customerReviews')}</h2>
              <p className="reviews-subtitle">
                {currentLang === 'bn'
                  ? 'আমাদের ক্রেতাদের সত ও নির্ভরযোগ্য রেটিং'
                  : 'Verified ratings & reviews from customer feedback'}
              </p>
            </div>
            <button
              type="button"
              className="write-review-toggle-btn"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {showReviewForm
                ? (currentLang === 'bn' ? '✕ বন্ধ করুন' : '✕ Close Form')
                : (currentLang === 'bn' ? '✍️ রিভিউ লিখুন' : '✍️ Write a Review')}
            </button>
          </div>

          {showReviewForm && (
            <div className="review-form-collapsible-wrapper">
              <ReviewForm
                productId={product.id}
                onReviewSubmitted={() => {
                  loadReviews(product.id);
                  setShowReviewForm(false);
                }}
              />
            </div>
          )}

          <ReviewList reviews={reviews} loading={reviewsLoading} />
        </section>
      </div>
    </div>
  );
};

export default ProductDetails;
